import React from 'react';
import loginBackground from '@/assets/auth/login-background.png';
import logoEu from '@/assets/auth/logo-eu.png';
import logoGovernment from '@/assets/auth/logo-government.png';
import logoProgramSanatate from '@/assets/auth/logo-program-sanatate.png';
import logoMsCurie from '@/assets/auth/logo-ms-curie.png';
import logoNeonat from '@/assets/auth/logo-neonat.png';
import logoPracticNng from '@/assets/auth/logo-practicnng.png';

interface AuthShellProps {
  children: React.ReactNode;
}

const topMarks = [
  { src: logoEu, alt: 'Cofinantat de Uniunea Europeana', className: 'h-[68px] w-auto' },
  { src: logoGovernment, alt: 'Guvernul Romaniei', className: 'h-[60px] w-auto' },
  { src: logoProgramSanatate, alt: 'Program Sanatate', className: 'h-[52px] w-auto' },
];

const footerMarks = [
  { src: logoMsCurie, alt: 'MS Curie' },
  { src: logoNeonat, alt: 'NeoNat' },
  { src: logoPracticNng, alt: 'PracticNNG' },
];

export const AuthShell: React.FC<AuthShellProps> = ({ children }) => {
  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-100 text-slate-950">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${loginBackground})` }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(255,255,255,0.28),_rgba(232,240,252,0.16)_35%,_rgba(12,43,98,0.18)_100%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(135deg,_rgba(0,48,135,0.12),_rgba(255,255,255,0)_42%,_rgba(244,164,74,0.14))]" />
      <div className="absolute inset-0 bg-[rgba(210,228,248,0.52)]" />
      <div className="absolute left-[-8rem] top-[-6rem] h-80 w-80 rounded-full bg-[#f4a44a]/20 blur-3xl" />
      <div className="absolute bottom-[-10rem] right-[-8rem] h-96 w-96 rounded-full bg-[#003087]/20 blur-3xl" />

      <div className="relative z-10 flex min-h-screen flex-col">
        <header className="border-b-2 border-[#003087] bg-white/90 px-4 py-4 shadow-[0_2px_14px_rgba(0,48,135,0.12)] backdrop-blur md:px-8">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-3 md:gap-5">
            {topMarks.map((mark, index) => (
              <React.Fragment key={mark.alt}>
                <div className="flex items-center justify-center px-3 md:px-6">
                  <img
                    src={mark.src}
                    alt={mark.alt}
                    className={`${mark.className} block object-contain max-md:h-11`}
                  />
                </div>
                {index < topMarks.length - 1 && (
                  <div className="hidden h-[52px] w-px bg-slate-300 md:block" aria-hidden="true" />
                )}
              </React.Fragment>
            ))}
          </div>
        </header>

        <main className="flex flex-1 items-center justify-center px-4 py-10 md:px-6 lg:px-10">
          <div className="w-full max-w-6xl">
            <div className="mb-6 text-center font-sans text-4xl font-black uppercase tracking-[0.5em] text-[#f4a44a] drop-shadow-[0_2px_14px_rgba(160,80,0,0.35)] md:text-5xl">
              REMCO
            </div>

            <div className="flex flex-col items-stretch gap-10 lg:flex-row lg:items-center lg:justify-between lg:gap-14">
              <section className="max-w-xl text-white">
                <div className="mb-5 border-b-2 border-white/40 pb-3 text-xs font-bold uppercase tracking-[0.2em] text-white/90">
                  Spitalul Clinic de Urgență pentru Copii „M.S. Curie"
                </div>
                <h1 className="max-w-lg text-3xl font-black uppercase leading-tight drop-shadow-[0_2px_10px_rgba(0,0,0,0.32)] md:text-4xl">
                  Registru Național de
                  <br />
                  Prematuritate și
                  <br />
                  Malformații Congenitale Cardiace
                </h1>
                <p className="mt-4 max-w-xl text-base leading-7 text-white/90 drop-shadow-[0_1px_4px_rgba(0,0,0,0.3)]">
                  dezvoltat în cadrul proiectului
                </p>
                <div className="mt-6 inline-block rounded-r-lg border-l-[3px] border-white/70 bg-[#003087]/55 px-4 py-3 text-sm leading-6 text-white/95 shadow-[0_10px_30px_rgba(0,48,135,0.18)] backdrop-blur">
                  <strong className="mb-1 block text-sm font-bold text-white">
                    „PracTINNg: Progres în resurse și asistență clinică pentru Terapia Intensivă
                    Nou-Născuți"
                  </strong>
                  finanțat prin PS 2021-2027
                  <div className="mt-3 inline-flex items-center gap-2 rounded-sm border border-white/40 bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#f0c000]" />
                    cod MySMIS 334162
                  </div>
                </div>
              </section>

              <div className="w-full max-w-md shrink-0">{children}</div>
            </div>
          </div>
        </main>

        <footer className="bg-[#003087]/90 px-4 py-4 backdrop-blur md:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="mb-3 flex flex-wrap items-center justify-center gap-5">
              {footerMarks.map((mark, index) => (
                <React.Fragment key={mark.alt}>
                  <img
                    src={mark.src}
                    alt={mark.alt}
                    className="h-[46px] w-auto object-contain opacity-85 brightness-0 invert transition hover:opacity-100"
                  />
                  {index < footerMarks.length - 1 && (
                    <span className="hidden h-9 w-px bg-white/25 md:inline-block" aria-hidden="true" />
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="border-t border-white/15 pt-3 text-center text-xs text-white/70">
              <strong className="text-white">REMCO</strong>
              {' '}
              | Proiect cofinantat de
              {' '}
              <strong className="text-white">Uniunea Europeana</strong>
              {' '}
              | © 2026
              {' '}
              <strong className="text-white">Guvernul Romaniei</strong>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};
